import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import LeftNav from './common/LeftNav';
import TopNav from './common/TopNav';
import taskService from '../../services/taskService';
import userService from '../../services/userService';
import '../assets/styles/AdminTask.css';
import '../assets/styles/dashboard.css';

// Icons (using available admin assets)
import filterIcon from '../assets/img/filter1.svg';
import addIcon from '../assets/img/addlead.svg';
import editIcon from '../assets/img/editadminweb.svg';

const AdminTask = () => {
  // Tab and Filter States (matches React Native app)
  const [activeTab, setActiveTab] = useState('Tasks');
  const [activeFilter, setActiveFilter] = useState('All');

  // API Integration States
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Create Task Form States (matches React Native app)
  const [taskOwner, setTaskOwner] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [taskType, setTaskType] = useState('');
  const [status, setStatus] = useState('pending');
  const [description, setDescription] = useState('');
  const [taskName, setTaskName] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');

  // Edit Task States (matches React Native app)
  const [selectedTask, setSelectedTask] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTaskType, setEditTaskType] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editTaskOwner, setEditTaskOwner] = useState('');

  // Filter States (matches React Native app)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');

  // Error and Success States
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Options (matches React Native app)
  const taskTypes = ['General', 'Lead Follow-up', 'Site Visit', 'Property Management', 'Customer Service'];
  const priorities = ['low', 'medium', 'high', 'urgent'];
  const statusOptions = ['pending', 'in_progress', 'completed', 'missed'];
  const filterOptions = ['All', 'Pending', 'In Progress', 'Completed', 'Missed', 'Today', 'This Week', 'Overdue'];

  // Load tasks and users on mount (matches React Native app)
  useEffect(() => {
    console.log('🚀 AdminTask component mounted, loading data...');
    loadTasks();
    loadAdminUsers();
  }, []);

  // Reload tasks when filter changes (matches React Native app)
  useEffect(() => {
    loadTasks();
  }, [activeFilter, fromDate, toDate, filterStatus, filterPriority, filterAssignedTo]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');

      // Build filters exactly like React Native app
      const filters = {};

      // Apply filter modal selections only (not activeFilter status)
      if (filterAssignedTo) filters.assigned_to = filterAssignedTo;
      if (filterPriority) filters.priority = filterPriority;
      if (filterStatus) filters.status = filterStatus;

      // Default: Show last 7 days tasks if no date filters are applied
      const hasDateFilters = fromDate || toDate;

      if (!hasDateFilters) {
        // Set default to last 7 days (matching React Native app)
        const today = new Date();
        const sevenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7, 0, 0, 0);
        const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        filters.created_date_from = sevenDaysAgo.toISOString();
        filters.created_date_to = endOfToday.toISOString();
      }

      // Convert date filters to ISO format if provided
      if (fromDate) {
        const fromDateObj = new Date(fromDate);
        filters.created_date_from = fromDateObj.toISOString();
      }
      if (toDate) {
        const toDateObj = new Date(toDate);
        toDateObj.setHours(23, 59, 59, 999); // End of day
        filters.created_date_to = toDateObj.toISOString();
      }

      console.log('📋 Filters being sent to API:', filters);

      const response = await taskService.getTasks(filters);
      console.log('✅ Tasks fetched:', response);

      // Parse response based on API structure (matching React Native app)
      let tasksList = [];
      if (response.success && response.data) {
        tasksList = Array.isArray(response.data) ? response.data :
                   Array.isArray(response.data.tasks) ? response.data.tasks : [];
      } else if (response.data && Array.isArray(response.data)) {
        tasksList = response.data;
      }

      console.log('📋 Final tasks list:', tasksList.length, 'tasks');

      setTasks(tasksList);

      if (tasksList.length === 0) {
        console.log('ℹ️ No tasks found for current filters');
      }

    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      console.log('👥 Loading admin users (matching React Native app)...');

      // Fetch all admin, manager, and staff users in parallel (matching React Native app)
      const [adminResponse, managerResponse, subUserResponse] = await Promise.all([
        userService.getAllUsers({ role: 'admin' }),
        userService.getAllUsers({ role: 'manager' }),
        userService.getAllUsers({ role: 'sub_user' })
      ]);

      let allUsers = [];

      // Process admin users (matching React Native app)
      if (adminResponse.success && adminResponse.data?.users) {
        const adminsList = adminResponse.data.users.map((user) => ({
          id: user.id,
          name: user.name || user.first_name || user.email || `User ${user.id}`,
          email: user.email,
          role: 'admin',
          displayName: `${user.name || user.first_name || user.email} (Admin)`
        }));
        allUsers = [...allUsers, ...adminsList];
      }

      // Process manager users (matching React Native app)
      if (managerResponse.success && managerResponse.data?.users) {
        const managerList = managerResponse.data.users.map((user) => ({
          id: user.id,
          name: user.name || user.first_name || user.email || `User ${user.id}`,
          email: user.email,
          role: 'manager',
          displayName: `${user.name || user.first_name || user.email} (Manager)`
        }));
        allUsers = [...allUsers, ...managerList];
      }

      // Process sub_user (staff) users (matching React Native app)
      if (subUserResponse.success && subUserResponse.data?.users) {
        const staffList = subUserResponse.data.users.map((user) => ({
          id: user.id,
          name: user.name || user.first_name || user.email || `User ${user.id}`,
          email: user.email,
          role: 'sub_user',
          displayName: `${user.name || user.first_name || user.email} (Staff)`
        }));
        allUsers = [...allUsers, ...staffList];
      }

      console.log('✅ Fetched users:', allUsers.length);
      setAdminUsers(allUsers);

    } catch (error) {
      console.error('Error fetching users:', error);
      // Set empty array on error - user can retry (matching React Native app)
      setAdminUsers([]);
    }
  };

  const handleCreateTask = async () => {
    try {
      if (!taskName || !taskType) {
        setError('Please fill in required fields: Task Name and Task Type');
        return;
      }

      setLoading(true);
      setError('');

      // Format due date to ISO string if provided (matching React Native app)
      let isoDate = null;
      if (dueDate) {
        const date = new Date(dueDate);
        date.setHours(23, 59, 59, 999); // Set to end of day
        isoDate = date.toISOString();
      } else {
        // If no due date selected, set to today's date to avoid null
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        isoDate = today.toISOString();
      }

      // Get task owner name from selected user ID
      const selectedUser = adminUsers.find(user => user.id === taskOwner);
      const taskOwnerName = selectedUser ? selectedUser.name : null;

      const taskData = {
        title: taskName,
        description: description,
        category: taskType, // Don't transform - send as is (matching React Native app)
        priority: priority,
        status: status,
        due_date: isoDate, // Always send date, never null
        assigned_to: taskOwner || null, // Send user ID (matching React Native app)
        task_owner: taskOwnerName, // Send task owner name for display
      };

      console.log('🔄 Creating task with data:', taskData);
      console.log('📋 Form state - taskOwner:', taskOwner, 'assignedTo:', assignedTo);
      console.log('👥 Available users:', adminUsers.map(u => ({id: u.id, name: u.name, displayName: u.displayName})));

      const response = await taskService.createTask(taskData);

      if (response.success) {
        setSuccess('Task created successfully!');
        setShowCreateModal(false);
        resetCreateForm();
        loadTasks(); // Reload tasks
      } else {
        setError(response.message || 'Failed to create task');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = async () => {
    try {
      if (!editTaskName || !editTaskType) {
        setError('Please fill in required fields');
        return;
      }

      setLoading(true);
      setError('');

      // Format due date to ISO string if provided (matching React Native app)
      let isoDate = null;
      if (editDueDate) {
        const date = new Date(editDueDate);
        date.setHours(23, 59, 59, 999); // Set to end of day
        isoDate = date.toISOString();
      } else {
        // If no due date selected, set to today's date to avoid null
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        isoDate = today.toISOString();
      }

      // Get task owner name from selected user ID
      const selectedUser = adminUsers.find(user => user.id === editTaskOwner);
      const taskOwnerName = selectedUser ? selectedUser.name : null;

      const taskData = {
        title: editTaskName,
        description: editDescription,
        category: editTaskType, // Don't transform - send as is (matching React Native app)
        priority: editPriority,
        status: editStatus,
        due_date: isoDate, // Always send date, never null
        assigned_to: editTaskOwner || null, // Send user ID (matching React Native app)
        task_owner: taskOwnerName, // Send task owner name for display
      };

      console.log('🔄 Updating task', selectedTask.id, 'with data:', taskData);

      const response = await taskService.updateTask(selectedTask.id, taskData);

      if (response.success) {
        setSuccess('Task updated successfully!');
        setShowEditModal(false);
        loadTasks(); // Reload tasks
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.message || 'Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await taskService.deleteTask(taskId);

      if (response.success) {
        setSuccess('Task deleted successfully!');
        loadTasks(); // Reload tasks
      } else {
        setError(response.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err.message || 'Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      setLoading(true);
      setError('');

      const response = await taskService.updateTaskStatus(taskId, newStatus);

      if (response.success) {
        setSuccess(`Task status updated to ${newStatus}!`);
        loadTasks(); // Reload tasks
      } else {
        setError(response.message || 'Failed to update task status');
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError(err.message || 'Failed to update task status');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setTaskOwner('');
    setTicketType('');
    setTaskType('');
    setStatus('pending');
    setDescription('');
    setTaskName('');
    setPriority('medium');
    setAssignedTo('');
    setDueDate('');
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setEditTaskName(task.title || '');
    setEditDescription(task.description || '');
    setEditTaskType(task.category || '');
    setEditPriority(task.priority || 'medium');
    setEditStatus(task.status || 'pending');
    setEditAssignedTo(task.assigned_to || '');
    setEditDueDate(task.due_date ? task.due_date.split('T')[0] : '');
    setEditTaskOwner(task.task_owner || '');
    setShowEditModal(true);
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'missed': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to filter tasks based on activeFilter (matching React Native app)
  const getFilteredTasks = () => {
    switch (activeFilter) {
      case 'Pending':
        return tasks.filter(t => t.status === 'pending');
      case 'In Progress':
        return tasks.filter(t => t.status === 'in_progress');
      case 'Completed':
        return tasks.filter(t => t.status === 'completed');
      case 'Missed':
        return tasks.filter(t => t.status === 'missed');
      case 'Today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tasks.filter(t => {
          const taskDate = new Date(t.created_at || t.due_date);
          return taskDate >= today && taskDate < tomorrow;
        });
      case 'This Week':
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return tasks.filter(t => {
          const taskDate = new Date(t.created_at || t.due_date);
          return taskDate >= weekStart && taskDate < weekEnd;
        });
      case 'Overdue':
        const now = new Date();
        return tasks.filter(t => {
          const dueDate = new Date(t.due_date);
          return dueDate < now && t.status !== 'completed';
        });
      case 'All':
      default:
        return tasks;
    }
  };

  console.log('🎨 AdminTask component rendering, tasks:', tasks.length, 'loading:', loading);

  return (
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />

        <div className="container-fluid p-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="page-title">Task Management</h2>
              <p className="text-muted mb-0">
                Showing {getFilteredTasks().length} of {tasks.length} tasks
                {activeFilter !== 'All' && ` (${activeFilter})`}
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilterModal(true)}
              >
                <img src={filterIcon} alt="Filter" width="16" className="me-1" />
                Filters
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
                disabled={loading}
              >
                <img src={addIcon} alt="Add" width="16" className="me-1" />
                Add Task
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => {setRefreshing(true); loadTasks();}}
                disabled={loading || refreshing}
              >
                {refreshing ? <Spinner size="sm" /> : '↻'} Refresh
              </button>
            </div>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Filter Tabs (matches React Native app) */}
          <div className="card p-2 mb-4" style={{
            backgroundColor: 'rgb(242, 242, 242)',
            borderRadius: '24px',
            border: 'none'
          }}>
            <div className="d-flex gap-1 flex-wrap">
              {filterOptions.map((filter) => (
                <button
                  key={filter}
                  className={`btn flex-fill ${
                    activeFilter === filter
                      ? "text-white"
                      : "btn-link text-muted"
                  }`}
                  style={{
                    backgroundColor: activeFilter === filter ? '#18181B' : 'transparent',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '11px',
                    fontWeight: activeFilter === filter ? '700' : '500',
                    minHeight: '36px',
                  }}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Tasks List */}
          <div className="row">
            {loading && tasks.length === 0 ? (
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            ) : getFilteredTasks().length === 0 ? (
              <div className="col-12 text-center py-5">
                <p className="text-muted">No tasks found</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create First Task
                </button>
              </div>
            ) : (
              getFilteredTasks().map((task) => (
                <div key={task.id} className="col-lg-4 col-md-6 col-12 mb-4">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="card-title mb-1">{task.task_title || task.title || 'Untitled Task'}</h6>
                        <div className="dropdown">
                          <button
                            className="btn btn-sm btn-outline-secondary dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                          >
                            Actions
                          </button>
                          <ul className="dropdown-menu">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => openEditModal(task)}
                              >
                                Edit Task
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleUpdateTaskStatus(task.id, 'completed')}
                                disabled={task.status === 'completed'}
                              >
                                Mark Complete
                              </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                Delete Task
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <p className="card-text small text-muted mb-2">
                        {task.description || 'No description'}
                      </p>

                      <div className="d-flex gap-2 mb-2">
                        <span className={`badge bg-${getPriorityBadgeColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className={`badge bg-${getStatusBadgeColor(task.status)}`}>
                          {task.status?.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="small text-muted">
                        <div>Due: {formatDate(task.due_date)}</div>
                        {task.assigned_to && <div>Assigned: {task.assigned_to}</div>}
                        {task.task_owner && <div>Owner: {task.task_owner}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Create Task Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Create New Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={taskName}
                      onChange={(e) => setTaskName(e.target.value)}
                      placeholder="Enter task name"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Type *</Form.Label>
                    <Form.Select
                      value={taskType}
                      onChange={(e) => setTaskType(e.target.value)}
                    >
                      <option value="">Select task type</option>
                      {taskTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter task description"
                />
              </Form.Group>

              <div className="row">
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      {priorities.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned To</Form.Label>
                    <Form.Select
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                    >
                      <option value="">Select user</option>
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.email}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Owner</Form.Label>
                    <Form.Select
                      value={taskOwner}
                      onChange={(e) => setTaskOwner(e.target.value)}
                    >
                      <option value="">Select task owner</option>
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName || user.name || user.email}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateTask}
              disabled={loading}
            >
              {loading ? <Spinner size="sm" className="me-1" /> : null}
              Create Task
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Task Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={editTaskName}
                      onChange={(e) => setEditTaskName(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Type *</Form.Label>
                    <Form.Select
                      value={editTaskType}
                      onChange={(e) => setEditTaskType(e.target.value)}
                    >
                      <option value="">Select task type</option>
                      {taskTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </Form.Group>

              <div className="row">
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value)}
                    >
                      {priorities.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned To</Form.Label>
                    <Form.Select
                      value={editAssignedTo}
                      onChange={(e) => setEditAssignedTo(e.target.value)}
                    >
                      <option value="">Select user</option>
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.email}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Task Owner</Form.Label>
                    <Form.Select
                      value={editTaskOwner}
                      onChange={(e) => setEditTaskOwner(e.target.value)}
                    >
                      <option value="">Select task owner</option>
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName || user.name || user.email}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEditTask}
              disabled={loading}
            >
              {loading ? <Spinner size="sm" className="me-1" /> : null}
              Update Task
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Filter Modal */}
        <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Filter Tasks</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">All Status</option>
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                    >
                      <option value="">All Priorities</option>
                      {priorities.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Assigned To</Form.Label>
                <Form.Select
                  value={filterAssignedTo}
                  onChange={(e) => setFilterAssignedTo(e.target.value)}
                >
                  <option value="">All Users</option>
                  {adminUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setFilterStatus('');
                setFilterPriority('');
                setFilterAssignedTo('');
              }}
            >
              Clear Filters
            </Button>
            <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default AdminTask;